import type { Bootgrid } from "./bootgrid";
import { ColumnFilter, DataType, Filter } from "./bootgridTypes";
import { luscinia } from "../luscinia";
import { isRecordPage } from "../Utils/isRecordPage";

const emptyBootgridData = {
    current: 1,
    rowCount: 10,
    rows: [],
    total: 0
};

export function getFilter(this: Bootgrid): Filter {
    let columnFilters = getColumnSearches.call(this);
    if (!this.isWizardPage()) {
        columnFilters = columnFilters.concat(this.advancedFilterManager.getColumnFilters());
    }
    return {
        searchPhrase: (this.container?.querySelector("input.search-field") as HTMLInputElement)?.value ?? "",
        columnFilters: columnFilters,
        columnSort: getColumnSort.call(this),
        rowCount: this.getSelectedRowCount(),
        currentPage: this.getCurrentPage(),
        displayedColumns: this.getDisplayedColumns(), // Used for export excel
    };
}

export function getColumnSearches(this: Bootgrid): ColumnFilter[] {
    let columnSearches: ColumnFilter[] = [];

    const tableHeaders = this.container.querySelectorAll('table > thead > tr > th[data-column-id]:not([data-column-id="commands"])');
    tableHeaders.forEach((header) => {
        const columnId = header.getAttribute("data-column-id");
        const inputEl = header.querySelector("input");

        if (inputEl && inputEl.value !== "") {
            let dataType: DataType = "String";

            const stringDateTypes = ["DateTime", "DateTimeOffset", "DateOnly"];
            const columnSearchDataType = (this.$bootgrid as any).bootgrid("getColumnSettings").find((column) => column.id === columnId)?.dataType;
            if (stringDateTypes.includes(columnSearchDataType)) {
                dataType = "StringDate";
            }

            const isCheckboxMultiSelect = this.getColumnFilterWidget(columnId) === "checkbox-multiselect";
            // "__empty__" is een placeholder voor de "(leeg)"-checkbox - een echte lege string
            // zou hier niet te onderscheiden zijn van "geen selectie" (zie inputEl.value !== ""hierboven).
            const columnFilter: ColumnFilter = {
                ColumnName: columnId,
                DataType: dataType,
                FilterValue: isCheckboxMultiSelect ? "includes_any" : "includes",
                Values: isCheckboxMultiSelect
                    ? inputEl.value.split(",").map((v) => v === "__empty__" ? "" : v)
                    : [inputEl.value],
            };

            columnSearches.push(columnFilter);
        }
    });

    return columnSearches;
}

export function getUrl(this: Bootgrid): string {
    const tableElement = this.container.querySelector(".luscinia-bootgrid") as HTMLTableElement;
    const filterColumnName = tableElement.getAttribute("data-luscinia-filtercolumnname");
    const parentFilterColumnName = tableElement.getAttribute("data-luscinia-parentfiltercolumnname");
    const baseUrl = tableElement.getAttribute("data-base-url");

    if (this.isWizardPage()) {
        return baseUrl + "/bootgrid";
    }

    if (isRecordPage() && filterColumnName) {
        const recordForm = document.getElementById("editModal0Form");
        let parentPropertyId = recordForm.dataset.lusciniaRecordId;
        const parentFilterElement = recordForm.querySelector(`[name='${parentFilterColumnName}']`) as HTMLInputElement;
        if (parentFilterElement) {
            parentPropertyId = parentFilterElement.value;
        }

        return `${tableElement.dataset.url}/${tableElement.dataset.lusciniaFiltercolumnname}?filter-value=${encodeURIComponent(parentPropertyId)}`;
    }

    if (filterColumnName !== "") {
        return tableElement.dataset.url;
    } else {
        return baseUrl + "/bootgrid";
    }
}

export async function submitFilter(this: Bootgrid): Promise<void> {
    let url = getUrl.call(this);
    if (url === "") {
        await (this.$bootgrid as any).bootgrid("reloadWithData", emptyBootgridData);
        return;
    }

    let filter = getFilter.call(this);
    let data: any = JSON.stringify(filter);
    let isFormData = false;

    if (this.isWizardPage()) {
        const formElement = luscinia.getActiveForm();
        data = luscinia.serializeFormObject(formElement);
        data.append("filters", JSON.stringify(filter.columnFilters));
        data.append("rowCount", this.getSelectedRowCount());
        data.append("columnSort", JSON.stringify(filter.columnSort));
        data.append("current", this.getCurrentPage());
        isFormData = true;
    }

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: isFormData ? {} : { "Content-Type": "application/json" },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        await (this.$bootgrid as any).bootgrid("reloadWithData", responseData);
    } catch (error) {
        console.error("Something went wrong while filtering the data.", error);
    }
}

export function getColumnSort(this: Bootgrid): Record<string, "asc" | "desc"> {
    const thEls = this.container.querySelectorAll(
        'table > thead > tr > th[data-column-id]:not([data-column-id="commands"])'
    ) as NodeListOf<HTMLElement>;
    let columnSort: Record<string, "asc" | "desc"> = {};
    for (let i = 0; i < thEls.length; i++) {
        const thEl = thEls[i];
        const iconEl = thEl.querySelector("i.fa");
        if (iconEl.classList.contains("fa-chevron-up") || iconEl.classList.contains("fa-chevron-down")) {
            const columnId = thEl.getAttribute("data-column-id");
            const order = iconEl.classList.contains("fa-chevron-up") ? "asc" : "desc";
            columnSort[columnId] = order;
            return columnSort;
        }
    }
    return columnSort;
}
